'use client';

import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import PageSectionType from '@alltrails/amplitude/enums/PageSectionType';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import { useDispatch, useSelector } from '@/utils/redux';
import { OVERVIEW_SECTION_ID } from '@/utils/constants';
import { setActiveSection } from '@/utils/trailPageSlice';
import SectionNavId from '@/utils/SectionNavId';
import { REVIEWS_SECTION_ID } from '@/utils/constants';

const TOP_OF_PAGE = 164; // 100 padding + 64 sticky nav height

type SectionViewedHandlerProps = {
  className?: string;
  id?: SectionNavId;
  observerOptions?: IntersectionObserverInit;
  sectionType?: PageSectionType;
};

const SectionViewedHandler = ({ children, className, id, observerOptions, sectionType }: PropsWithChildren<SectionViewedHandlerProps>) => {
  const dispatch = useDispatch();
  const isScrollingViaTabs = useSelector(state => state.trailPage.isScrollingViaTabs);
  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, sectionType, 'trail page', observerOptions);

  const handleScroll = useCallback(() => {
    const refRect = ref.current?.getBoundingClientRect();
    if (!refRect) return;
    const { bottom, top } = refRect;

    // https://alltrails.atlassian.net/browse/DISCO-11047
    // This fixes a bug where clicking on a tab causes the page to scroll past other sections,
    // which update activeSection, causing the indicator on the tab bar to jump around.
    if (isScrollingViaTabs) {
      return;
    }

    if (id && (Math.abs(top) <= TOP_OF_PAGE || (top < 0 && bottom > TOP_OF_PAGE))) {
      dispatch(setActiveSection(id));
    } else if (id === OVERVIEW_SECTION_ID && top > TOP_OF_PAGE) {
      dispatch(setActiveSection(OVERVIEW_SECTION_ID));
    }
  }, [dispatch, id, isScrollingViaTabs]);

  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, 300);
    document.addEventListener('scroll', debouncedScroll);
    return () => document.removeEventListener('scroll', debouncedScroll);
  });

  return (
    <div ref={ref} className={className} id={id} tabIndex={id === REVIEWS_SECTION_ID ? -1 : undefined}>
      {children}
    </div>
  );
};

export default SectionViewedHandler;
